import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

interface StudioListViewProps {
  templates: StudioTemplateMessage[];
  loadedTemplates: boolean;
}

export default function StudioListView({
  templates,
  loadedTemplates,
}: StudioListViewProps) {
  const navigate = useNavigate();
  const { search } = window.location;

  return (
    <Box sx={{ padding: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 3 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Cami Studio
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() =>
              navigate(
                `/studio/new?mode=train${search ? `&${search.slice(1)}` : ""}`,
              )
            }
          >
            Train New Template
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              navigate(
                `/studio/new?mode=parse${search ? `&${search.slice(1)}` : ""}`,
              )
            }
          >
            Parse Document
          </Button>
        </Stack>
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 3 }}
      >
        Build parsers for new document formats without writing code. Upload a
        PDF, review the extracted draft, map fields to entities, and save the
        template — future uploads with the same format will parse automatically.
      </Typography>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loadedTemplates && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loadedTemplates && templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No templates yet. Click <strong>New Template</strong> to
                    upload your first document.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {templates.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.sourceName ?? "—"}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography
                    variant="body2"
                    color={t.notes ? "text.primary" : "text.secondary"}
                  >
                    {t.notes ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={t.status}
                    color={t.status === "ACTIVE" ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>{new Date(t.updatedAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
